// src/stores/chatStore.ts
// Zustand store for chat state — survives tab switches (component unmount/remount)

import { create } from 'zustand';
import { Message } from '../types/chat';
import { ThinkingStep } from '../components/ThinkingSteps';
import { ExecutionStage } from '../components/ExecutionStages';
import { PathLaunchContext } from '../types/paths';

// Concierge: tracks a pending routing suggestion so the next YES from the student
// can dispatch to the right surface (Bookmate / Workflow / ...).
export interface PendingSuggestion {
  surface: 'bookmate' | 'workflow';
  originalPrompt: string;
  createdAt: number;   // ms epoch; used to expire stale suggestions
}

interface ChatState {
  // Session
  currentSessionId: string | null;
  messages: Message[];
  input: string;
  selectedFiles: File[];

  // UI state that should survive tab switch
  workspaceModalOpen: boolean;
  workspaceContent: any;
  thinkingSteps: ThinkingStep[];
  executionStages: ExecutionStage[];
  isStreamingActive: boolean;
  loading: boolean;
  statusMessage: string;
  statusProgress: number;

  // Concierge routing
  pendingSuggestion: PendingSuggestion | null;

  // Set when this chat was opened from a Learning Path "Start →" deep link
  // (/codemate?prompt=...&path=...&node=...) — drives the "Back to your path"
  // banner. Cleared on new session / dismiss.
  pathContext: PathLaunchContext | null;

  // Actions
  setCurrentSessionId: (id: string | null) => void;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  setInput: (input: string) => void;
  setSelectedFiles: (files: File[] | ((prev: File[]) => File[])) => void;
  setWorkspaceModalOpen: (open: boolean) => void;
  setWorkspaceContent: (content: any) => void;
  setThinkingSteps: (steps: ThinkingStep[] | ((prev: ThinkingStep[]) => ThinkingStep[])) => void;
  setExecutionStages: (stages: ExecutionStage[] | ((prev: ExecutionStage[]) => ExecutionStage[])) => void;
  setIsStreamingActive: (active: boolean) => void;
  setLoading: (loading: boolean) => void;
  setStatusMessage: (message: string) => void;
  setStatusProgress: (progress: number) => void;
  setPendingSuggestion: (s: PendingSuggestion | null) => void;
  setPathContext: (ctx: PathLaunchContext | null) => void;

  // Bulk operations
  clearChat: () => void;
  startNewChat: (sessionId: string | null) => void;
}

export const useChatStore = create<ChatState>((set) => ({
  // Initial state
  currentSessionId: null,
  messages: [],
  input: '',
  selectedFiles: [],
  workspaceModalOpen: false,
  workspaceContent: null,
  thinkingSteps: [],
  executionStages: [],
  isStreamingActive: false,
  loading: false,
  statusMessage: '',
  statusProgress: 0,
  pendingSuggestion: null,
  pathContext: null,

  // Actions
  setCurrentSessionId: (id) => set({ currentSessionId: id }),
  setMessages: (messages) =>
    set((state) => ({
      messages: typeof messages === 'function' ? messages(state.messages) : messages,
    })),
  setInput: (input) => set({ input }),
  setSelectedFiles: (files) =>
    set((state) => ({
      selectedFiles: typeof files === 'function' ? files(state.selectedFiles) : files,
    })),
  setWorkspaceModalOpen: (open) => set({ workspaceModalOpen: open }),
  setWorkspaceContent: (content) => set({ workspaceContent: content }),
  setThinkingSteps: (steps) =>
    set((state) => ({
      thinkingSteps: typeof steps === 'function' ? steps(state.thinkingSteps) : steps,
    })),
  setExecutionStages: (stages) =>
    set((state) => ({
      executionStages: typeof stages === 'function' ? stages(state.executionStages) : stages,
    })),
  setIsStreamingActive: (active) => set({ isStreamingActive: active }),
  setLoading: (loading) => set({ loading }),
  setStatusMessage: (message) => set({ statusMessage: message }),
  setStatusProgress: (progress) => set({ statusProgress: progress }),
  setPendingSuggestion: (s) => set({ pendingSuggestion: s }),
  setPathContext: (ctx) => set({ pathContext: ctx }),

  // Clear everything (logout)
  clearChat: () =>
    set({
      currentSessionId: null,
      messages: [],
      input: '',
      selectedFiles: [],
      workspaceModalOpen: false,
      workspaceContent: null,
      thinkingSteps: [],
      executionStages: [],
      isStreamingActive: false,
      loading: false,
      statusMessage: '',
      statusProgress: 0,
      pendingSuggestion: null,
      pathContext: null,
    }),

  // Start fresh session (new chat button)
  startNewChat: (sessionId) =>
    set({
      currentSessionId: sessionId,
      messages: [],
      input: '',
      selectedFiles: [],
      workspaceModalOpen: false,
      workspaceContent: null,
      thinkingSteps: [],
      executionStages: [],
      isStreamingActive: false,
      loading: false,
      statusMessage: '',
      statusProgress: 0,
      pendingSuggestion: null,
      pathContext: null,
    }),
}));
